import { motion, AnimatePresence } from "framer-motion";
import { Mic2, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Floating CTA that appears after a Jam Mode session loads.
 * Invites the user to record their voice over the jam → clone → AI-sing lyrics.
 *
 * Dismissable. Stays out of the way once dismissed.
 */
export default function SingOverItCTA({ open, jamInfo, onOpen, onDismiss }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="singoveritcta-overlay"
          data-testid="sing-over-it-cta"
          initial={{ y: 24, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 24, opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 320, damping: 24 }}
          className="fixed bottom-6 right-6 z-40 max-w-sm rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/20 via-cyan-500/15 to-fuchsia-500/15 p-4 shadow-2xl backdrop-blur-md"
        >
          <button
            data-testid="sing-over-it-dismiss"
            onClick={onDismiss}
            className="absolute right-2 top-2 rounded-md p-1 text-white/60 transition hover:bg-white/10 hover:text-white"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="flex items-start gap-3 pr-4">
            <div className="flex-shrink-0 rounded-xl border border-white/10 bg-black/30 p-2">
              <Sparkles className="h-4 w-4 text-emerald-300" />
            </div>
            <div>
              <p className="font-display text-sm font-bold leading-tight text-white">
                Sing over your jam
              </p>
              <p className="mt-1 text-[12px] leading-snug text-white/75">
                {jamInfo?.genre || "Your backing track"} is looping at {jamInfo?.tempo || 120} BPM.
                Record yourself, clone your voice, then have it sing any lyrics — in time.
              </p>
            </div>
          </div>
          <Button
            data-testid="sing-over-it-open"
            onClick={onOpen}
            className="mt-3 w-full rounded-xl bg-gradient-to-r from-emerald-400 via-cyan-400 to-fuchsia-400 text-black hover:opacity-90"
          >
            <Mic2 className="mr-2 h-4 w-4" />Open voice studio
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
